<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/main/PosMainIncomingPane.vue.
  // Data = ordersStore.newOrders; accept runs through useOrders().setOrderStatus
  // which already applies the caja/paid guards. While one accept/reject is in
  // flight the other cards' buttons stay locked.
  import { computed, getCurrentInstance, ref } from 'vue';
  import { OrderStatus } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOrder } from '@/interfaces/IOrder';
  import { maybePromptCoversAfterAccept } from '@/composition/covers-accept-prompt';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { useUi } from '@/composition/use-ui';
  import { useOrdersStore } from '@/stores/orders.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import PosV2IncomingPreviewModal from '@/components/pos-v2/modals/PosV2IncomingPreviewModal.vue';
  import PosV2IncomingCard from './PosV2IncomingCard.vue';

  const ordersStore = useOrdersStore();
  const ordersHelper = useOrders();
  const ui = useUi();
  const modals = useModal();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isLoading = computed(() => !ordersStore.newOrders);
  const orders = computed<IOrder[]>(() => ordersStore.newOrders ?? []);

  const acceptingOrderId = ref<number | null>(null);
  const rejectingOrderId = ref<number | null>(null);

  const isMutating = computed(
    () => acceptingOrderId.value !== null || rejectingOrderId.value !== null
  );

  function isUnseen(order: IOrder): boolean {
    return ordersStore.unseenOrderIdsSet.has(order.id);
  }

  function markSeen(order: IOrder) {
    ordersStore.removeUnseenOrder(order.id);
  }

  function handleView(order: IOrder) {
    markSeen(order);
    // Read-only preview with Aceptar/Rechazar in the footer (plan §5.4). The
    // modal runs the same accept/reject handlers and toasts as this pane.
    // clickToClose disabled: the modal's isMutating gate must cover EVERY
    // unmount path (an overlay click could otherwise tear it down while an
    // accept/reject/summary-print is in flight).
    modals.showDynamicDialog(
      PosV2IncomingPreviewModal,
      { orderId: order.id },
      'pos-v2-incoming-preview',
      { width: 1100, clickToClose: false }
    );
  }

  async function handleAccept(order: IOrder) {
    if (isMutating.value) return;

    try {
      acceptingOrderId.value = order.id;
      const didUpdate = await ordersHelper.setOrderStatus(
        order,
        OrderStatus.Progress
      );
      if (!didUpdate) return;

      ui.showToast(AlertType.Success, i18nT('posV2.orderAccepted'), {
        text: i18nT('posV2.viewOrder'),
        cb: () => ordersHelper.openOrder(order),
      });
      // Fire-and-forget covers confirmation when this accept opened the table.
      void maybePromptCoversAfterAccept(proxy.$modal, order);
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
    } finally {
      acceptingOrderId.value = null;
    }
  }

  async function handleReject(order: IOrder) {
    if (isMutating.value) return;

    const confirmed = await modals.confirm({
      title: i18nT('posV2.rejectOrderTitle'),
      message: i18nT('posV2.rejectOrderConfirm'),
    });
    // Re-check: an accept may have started while the confirm dialog was open.
    if (!confirmed || isMutating.value) return;

    try {
      rejectingOrderId.value = order.id;
      await ordersHelper.setOrderStatus(order, OrderStatus.Declined);
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
    } finally {
      rejectingOrderId.value = null;
    }
  }
</script>

<template>
  <div class="pos-v2-incoming-pane">
    <div class="pane-header">
      <h3 class="pane-title">
        <PosUiIcon name="inbox" class="pane-title-icon" />
        {{ $t('posV2.incomingTitle') }}
      </h3>
      <PosUiBadge
        v-if="orders.length > 0"
        :label="orders.length"
        color="error"
        variant="solid"
        size="sm"
      />
    </div>

    <div v-if="isLoading" class="loading-state">
      <PosUiSkeleton v-for="i in 2" :key="i" class="card-skeleton" />
    </div>

    <PosUiScrollable
      v-else
      class="pane-body"
      orientation="vertical"
      variant="overlay"
      theme="elevated"
      size="sm"
      scroll-class="pane-scroll"
    >
      <div v-if="orders.length > 0" class="orders-list">
        <PosV2IncomingCard
          v-for="order in orders"
          :key="order.id"
          :order="order"
          :unseen="isUnseen(order)"
          :accept-loading="acceptingOrderId === order.id"
          :reject-loading="rejectingOrderId === order.id"
          :accept-disabled="isMutating && acceptingOrderId !== order.id"
          :reject-disabled="isMutating && rejectingOrderId !== order.id"
          @view="handleView"
          @accept="handleAccept"
          @reject="handleReject"
          @seen="markSeen"
        />
      </div>

      <PosUiEmptyState
        v-else
        icon="inbox"
        :title="$t('posV2.incomingEmptyTitle')"
        :description="$t('posV2.incomingEmptyDescription')"
        class="empty-state"
      />
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-incoming-pane {
    @apply tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-bg-canvas;

    .pane-header {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3;

      .pane-title {
        @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-neutral-900;

        .pane-title-icon {
          @apply tw-text-lg tw-text-neutral-400;
        }
      }
    }

    .loading-state {
      @apply tw-flex tw-flex-col tw-gap-3 tw-p-3;

      .card-skeleton {
        @apply tw-h-32 tw-rounded-xl;
      }
    }

    .pane-body {
      @apply tw-relative tw-flex-1 tw-overflow-hidden;

      :deep(.pane-scroll) {
        @apply tw-h-full tw-overflow-y-auto;

        .orders-list {
          @apply tw-flex tw-flex-col tw-gap-3 tw-p-3;
        }

        .empty-state {
          @apply tw-h-full;
        }
      }
    }
  }
</style>
